<!--
Login screen
@author bmk11u
-->
<template>
  <div>
    <div id="loginLogo">
      <img class="loginLogoImg" src="@/assets/UofG_App_Logo.svg" alt="University of Glasgow Logo" height="110"/>
    </div>
    <Spinner ref="spinner_ref" />
    <main :key="disableLogin" class="p-2" style="margin-left:24px;margin-right:24px;text-align:center;">
        <!-- WCSC-2289 password managers don't seem to like Vue's two-way bindings so remove them -->
        <input autocomplete="username" class="loginElement" placeholder="Enter GUID e.g. 24444444Y or abc12d" id="guid" autofocus autocapitalize="off">
        <input autocomplete="current-password" type="password" class="loginElement"
          placeholder="Enter Password" id="password" @keyup.enter="login">
      <div v-if="errorMsg !== ''" class="error-message">
        {{ errorMsg }}
      </div>
      <div v-if="showLoginError" class="error-message">
        <p>Username or password is incorrect.</p>
        <p>Trouble logging in? <a href="https://www.gla.ac.uk/it/helpdesk/" target="_blank">Get help from the support team</a>.</p>
      </div>
      <!-- <div class="form-group form-check">
        <input type="checkbox" class="form-check-input" v-model="rememberMe" id="rememberMe">
        <label class="form-check-label" for="rememberMe">Remember me on this device</label>
      </div> -->
      <button :class="disableLogin ? 'loginButtonDisabled' : 'loginButton'" :disabled="disableLogin" @click="checkLogin">Log in</button>
      <a class="privacyStatement" href="http://www.gla.ac.uk/legal/privacy/" target="_new">Privacy and cookies</a>
      <AppStoreLink />
    </main>
  </div>
</template>

<script setup>
// import Logo from '@/components/Logo.vue'
import axios from 'axios'
import { ref, onMounted } from 'vue'
// import $cookies from 'vue-cookies'
import AppStoreLink from '@/components/AppStoreLink.vue'
import { findRoomId } from '@/mixins/CustomUrls.js'
import * as lst from '@/components/localStorage.js'
import { useRouter } from 'vue-router'

const router = useRouter()

// Component Refs
const spinner_ref = ref(null)
const disableLogin = ref(false)
const rememberMe = ref(true)
const errorMsg = ref('')
const buttonClicked = ref(false) // stop enabledLogin check from firing when we click submit
const showLoginError = ref(false)

onMounted(() => {
// if we're being launched in a mobile browser
    // offer to launch ourselves in the mobile app
    // (4.3 and up)
    if (isInMobileBrowser() && lst.hasKey('requestedView')) {
      // requestedView key gets set in router.js if we got redirected to login screen
      const reqView = lst.getValue('requestedView')
      console.log('*this is a mobile browser* - requestedView is ' + reqView)
      if (reqView.indexOf('/findaspace') !== -1 && reqView.indexOf('roomId') !== -1) {
        // it's probably a signage link - e.g. /findaspace?roomId=1730659&slot=now&src=sng
        const roomId = findRoomId(reqView)
        if (typeof roomId !== 'undefined') {
          location.href = 'uofglife://booking?src=sng&roomId=' + roomId
        } else {
          console.error('Given a findaspace link with room but could not find roomId')
        }
      }
    }
    // Checks cached login token
    if (lst.hasKey('guid')) {
      spinner_ref.value.spin()
      var passGuid = lst.getValue('guid')
      var passToken = lst.getValue('token')
      axios.put('/timetable/login', {
        guid: lst.getValue('guid'),
        token: lst.hasKey('token') ? lst.getValue('token') : 'NONE!'
      })
        .then(response => {
          if (response.data.skipLogin) {
            setCookiesAndForward(response, passGuid, passToken)
          }
        }).finally(() => {
          if (spinner_ref.value) {
            spinner_ref.value.stop()
          }
        })
    }
})

function isInMobileBrowser () {
  const toMatch = [
    /Android/i,
    /webOS/i,
    /iPhone/i,
    /iPad/i,
    /iPod/i,
    /BlackBerry/i,
    /Windows Phone/i
  ]

  return toMatch.some((toMatchItem) => {
    return navigator.userAgent.match(toMatchItem)
  })
}

function checkLogin () {
  if (document.getElementById('password').value === '') {
    errorMsg.value = 'Please enter a password'
  } else {
    login()
  }
}

// Makes a login request
function login () {
  disableLogin.value = true
  buttonClicked.value = true
  showLoginError.value = false
  spinner_ref.value.spin()
  axios.post('/timetable/login', {
    guid: document.getElementById('guid').value, // guid,
    password: document.getElementById('password').value, // password,
    rememberMe: rememberMe.value
  })
    .then(response => {
      if (rememberMe.value) {
        let expires = new Date()

        expires.setTime(expires.getTime() + (response.data.cachedLogin.expires * 1000))
        lst.setValue('guid', response.data.cachedLogin.guid) //, expires.toUTCString()
        lst.setValue('token', response.data.cachedLogin.token) //, expires.toUTCString()
        // if we have a mapped guid let's include that also
        if (typeof response.data.mappedGuid !== 'undefined') {
          lst.setValue('mappedGuid', response.data.mappedGuid) //, expires.toUTCString())
        }
      }
      if (!(process.env.VUE_APP_MODE === 'hybrid')) {
        axios.post('/timetable/notifications', { token: null }) // this is here to limit # calls to server
      }
      lst.setValue('showNotifications', true)
      // and update...
      // lst.setValues(cookies)
      setCookiesAndForward(response)
    })
    .catch((error) => {
      console.log(error)
      disableLogin.value = false
      buttonClicked.value = false
      showLoginError.value = true
    })
    .finally(() => {
      if (spinner_ref.value) {
        spinner_ref.value.stop()
      }
      window.addEventListener('beforeunload', function () {
        buttonClicked.value
      })
    })
}
// sends firebase token to db. checks to see if android or ios device.
function setNotificationToken () {
  if (process.env.VUE_APP_MODE === 'hybrid') {
    if (typeof window.FirebasePlugin !== 'undefined') {
      console.log(typeof window.FirebasePlugin)
      // Check permission.
      window.FirebasePlugin.hasPermission(
        hasPermission => {
          if (hasPermission) {
            // iOS and Android.
            storeFirebaseToken()
          } else {
            if (navigator.userAgent.match(/iPhone|iPad/)) {
            // Seek permission on iOS on first time use.
              window.FirebasePlugin.grantPermission(
                hasPermission => {
                  if (hasPermission) {
                    storeFirebaseToken()
                  } else {
                    storeNotAllowedToken()
                  }
                },
                err => {
                  alert(err)
                })
            } else {
            // Android.
              storeNotAllowedToken()
            }
          }
        },
        err => {
          alert(err)
        })
    } else {
      console.error('in hybrid mode but window.FirebasePlugin is not defined!')
    }
  }
}
    // Gets this device's token from Firebase and if it is new then store on the client and server.
function storeFirebaseToken () {
  window.FirebasePlugin.getToken(
    fcmToken => {
      if (lst.getValue('notificationToken') != fcmToken) {
        axios.post('/timetable/notifications', { 'token': fcmToken })
          .then(() => {
            // $cookies.set('notificationToken', fcmToken, '364d')
            lst.setValue('notificationToken', fcmToken)
          })
      }
    },
    err => {
      alert(err)
    })
}
// Stores the token as "notAllowed" on client and server. The server will ignore these and not send a message.
function storeNotAllowedToken () {
  if (lst.getValue('notificationToken') != 'notAllowed') {
    axios.post('/timetable/notifications', { 'token': 'notAllowed' })
      .then(() => {
        lst.setValue('notificationToken', 'notAllowed')
      })
  }
}

function setAdditionalInfo (response) {
  let expires = new Date()
  // console.log(expires.getTime())
  // console.log(response.data)

  if (typeof response.data.cachedLogin !== 'undefined') {
  //  console.log(response.data.cachedLogin.expires)
  //  console.log(response.data.cachedLogin.expires * 1000)
    expires.setTime(expires.getTime() + (response.data.cachedLogin.expires * 1000))
  } else {
    expires.setTime(expires.getTime() + (60 * 60 * 24 * 365 * 1000))
  }

  // and default booking times for the new screen
  /* if (typeof response.data.bookingTimes !== 'undefined') {
    lst.setValue('bookingStartHour', response.data.bookingTimes.startHour) // , expires.toUTCString())
    lst.setValue('bookingEndHour', response.data.bookingTimes.endHour) // , expires.toUTCString())
  } */
}
// Sets cookies and forwards to requested screen.
function setCookiesAndForward (response, newGuid, newToken) {
  var ms = Date.now()

  if (lst.getValue('logoutActivated') === true) {
    lst.setValue('logoutActivated', false)
    router.push('/logout')
  }

  // TEST indexedDb code
  const rd = response.data
  const hasCachedLogin = typeof rd.cachedLogin !== 'undefined'
  // this.errorMsg = 'DEBUG: *** newGuid = ' + newGuid + (hasCachedLogin ? 'there is login data' : 'no cached login :-/')
  let cachedLoginGuid = newGuid
  let cachedLoginToken = newToken
  let nonnullCLG = (typeof cachedLoginGuid !== 'undefined' && cachedLoginGuid != null)
  let nonnullCLT = (typeof cachedLoginToken !== 'undefined' && cachedLoginToken != null)
  if (hasCachedLogin) {
    cachedLoginGuid = rd.cachedLogin.guid
    cachedLoginToken = rd.cachedLogin.token
    nonnullCLG = (typeof cachedLoginGuid !== 'undefined' && cachedLoginGuid !== null)
    nonnullCLT = (typeof cachedLoginToken !== 'undefined' && cachedLoginToken !== null)

    console.log('there is a cached login with cached guid ' + cachedLoginGuid)
  }

  const idata = {
    'authenticated': true, /* only used to tip off the UI there is a logged-in user */
    'showHomepageNotice': true,
    'guid': nonnullCLG ? cachedLoginGuid : lst.getValue('guid'),
    // WCSC-2119 removing token seems to be causing trouble with skipLogin
    //           so we'll keep it in for now
    // WCSC-2118 i don't understand why lst.getValue('guid') has a value in mounted() but is empty here :-/
    'token': nonnullCLT ? cachedLoginToken : lst.getValue('token'),
    'mappedGuid': rd.mappedGuid,
    'timetableDefaultView': rd.timetableDefaultView,
    'hasRoomBookingAccess': rd.hasRoomBookingAccess,
    'hasDeskBookingAccess': rd.hasDeskBookingAccess,
    'name': rd.firstname,
    'showPopupStudySpace': rd.showPopupStudySpace,
    'assessmentDataAvailable': rd.assessmentDataAvailable,
    'showAssessmentAndFeedback': rd.showAssessmentAndFeedback,
    'noTimetable': rd.noTimetable,
    'showLecturer': rd.showLecturer,
    'featureList': rd.featureList,
    'cacheClearNeeded': rd.cacheClearNeeded,
    'unreadNotifications': rd.unreadNotifications,
    'libraryHours': rd.libraryHours,
    /* 'bookingTimes': rd.bookingTimes, */
    'userRoles': rd.userRoles,
    'indexTime': ms,
    'timetableTime': ms,
    'footerMenu': JSON.stringify(rd.footerMenu),
    'showLibraryInformation': rd.showLibraryInformation
  }
  lst.setValues(idata)
  console.log('data saved for guid ' + idata['guid'])
  // END TEST
  setAdditionalInfo(response)
  checkForMaintScreenAnalytics()
  console.log('pushing requested view')
  setNotificationToken()
  router.push(getRequestedView())
}
// Returns the request view
function getRequestedView () {
  let requestedView = '/'
  if (lst.hasKey('requestedView')) {
    requestedView = lst.getValue('requestedView')
    // delete this.cookies['requestedView']
    lst.deleteValue('requestedView')
  }
  return requestedView
}

function checkForMaintScreenAnalytics() {
  if (lst.hasKey('maintscreens')) {
    const maintscreens = lst.getValue('maintscreens')
    // send to server for recording/analysis/etc
    console.log('found unsent maint screen records...')
    axios.post('/timetable/public/maintscreen/report', maintscreens)
    .then(response => {
      if (typeof response.data !== 'undefined') {
        console.log(response.data)
      }
    })
    .finally(() => {
      // delete it
      console.log('clearing maint screen records...')
      lst.deleteValue('maintscreens')
    })
  }
}
</script>

<style scoped>
.error-message {
  color: red;
  margin-bottom: 10px;
}

#loginLogo {
  height:200px;
  background-color: var(--uog-dark-blue);
  text-align:center;
}

.loginLogoImg {
  margin-top: 60px;
  display:inline-block;
  width:110px;
  height: 110px;
  padding: 10px 29px;
}

.guid-help {
  margin-bottom: 0px;
  text-align: left;
}

.loginElement {
  background: rgba(118, 118, 128, 0.12);
  border-radius: 10px;
  height:52px;
  font-family:PTSans;
  border:0px rgba(118, 118, 128, 0.12);
  padding-left:12px;
  padding-right:12px;
  margin-top:12px;
  width:100%;

}

.loginButtonDisabled {
  background-color:#C3C5CB;
  width:100%;
  color:#ffffff;
  font-family:PTSans;
  border: 1px solid #C3C5CB;
  border-radius:12px;
  height:52px;
  margin-top:24px;
  margin-bottom:21px;
  font-weight:700;
}

.loginButton {
  background-color: var(--uog-dark-blue);
  width:100%;
  color:#ffffff;
  font-family:PTSans;
  border: 1px solid var(--uog-dark-blue);
  border-radius:12px;
  height:52px;
  margin-top:24px;
  margin-bottom:21px;
  font-weight:700;
}

.loginElement::placeholder {
  color:#4F5961;
}

.loginElement:focus {
 outline: 1px solid #951272 !important;
}

.privacyStatement {
  color: var(--uog-dark-blue);
  font-weight:bold;

}
</style>
